import {
  API_AI_CONNECTOR,
  API_AI_CONNECTOR_AUTHORIZE,
  API_AI_CONNECTOR_CONNECT,
  API_AI_CONNECTOR_REDIRECT_URI,
  API_AI_CONNECTORS,
  API_AI_CONNECTORS_CATALOG,
  API_AI_PROJECT_CONNECTORS,
} from 'config/api.url.constants'
import Api from 'services/api'

export type AiConnectorStatus = 'draft' | 'needs_auth' | 'connected' | 'error'

export interface AiConnector {
  id: number
  name: string
  description: string
  url: string
  catalogKey?: string
  authType: 'none' | 'token' | 'oauth'
  status: AiConnectorStatus
  allowWrites: boolean
  isEnabled: boolean
  isAlwaysOn: boolean
  /** True when a token or OAuth credential is stored. The credential itself never leaves the API. */
  hasCredential: boolean
  toolCount: number
  /** How many of the connector's tools the server marks read-only. */
  readOnlyToolCount: number
  /** False means the server never says which tools only read, so none are usable while writes are off. */
  annotatesReadOnly: boolean
  lastConnectedAt?: string
  lastError?: string
  createdBy: string
}

export interface AiConnectorCatalogEntry {
  key: string
  name: string
  url: string
  description: string
  /** This vendor does not register clients automatically, so connecting needs a client id from them. */
  needsClientId?: boolean
}

export const getAiConnectors = (): Promise<AiConnector[]> => Api.get(API_AI_CONNECTORS)

export const getAiConnectorCatalog = (): Promise<AiConnectorCatalogEntry[]> => Api.get(API_AI_CONNECTORS_CATALOG)

export const createAiConnector = (data: {
  name: string
  url: string
  description?: string
  token?: string
  catalogKey?: string
  oauthClientId?: string
  oauthClientSecret?: string
}): Promise<AiConnector> => Api.post(API_AI_CONNECTORS, data)

/** The redirect URL to register with vendors that need a manually created OAuth client. */
export const getAiConnectorRedirectUri = (): Promise<{ redirectUri: string }> =>
  Api.get(API_AI_CONNECTOR_REDIRECT_URI)

export const updateAiConnector = (id: number, data: Partial<AiConnector> & { token?: string }): Promise<AiConnector> =>
  Api.put(API_AI_CONNECTOR, data, { id })

export const deleteAiConnector = (id: number): Promise<{ success: boolean }> => Api.delete(API_AI_CONNECTOR, {}, { id })

/** Re-reach the connector and refresh its tool list. */
export const connectAiConnector = (id: number): Promise<AiConnector> => Api.post(API_AI_CONNECTOR_CONNECT, {}, { id })

/** Begin authorization. Returns the URL to open so the admin can sign in to the connector. */
export const authorizeAiConnector = (id: number): Promise<{ authorizeUrl?: string; alreadyConnected?: boolean }> =>
  Api.post(API_AI_CONNECTOR_AUTHORIZE, {}, { id })

export const getProjectConnectors = (projectId: number): Promise<number[]> =>
  Api.get(API_AI_PROJECT_CONNECTORS, {}, { id: projectId })

export const setProjectConnectors = (projectId: number, connectorIds: number[]): Promise<{ connectorIds: number[] }> =>
  Api.put(API_AI_PROJECT_CONNECTORS, { connectorIds }, { id: projectId })
